import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Calendar, CreditCard, ChevronDown } from "lucide-react";
import { Sparks } from "@/components/common/Sparks";
import { Smoke } from "@/components/common/Smoke";
import { Particles } from "@/components/common/Particles";
import { BlueprintLines } from "@/components/common/BlueprintLines";
import { Container } from "@/components/ui/Container";
import { Button } from "@/components/ui/Button";
import { useScrollTo } from "@/hooks/useScrollTo";
import { BUSINESS, logo, hero } from "@/utils/constants";

const LINES = ["BUILT STRONG.", "BUILT RIGHT."];
const EASE = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const scrollTo = useScrollTo();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden text-center">
      {/* Parallax background photo */}
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img src={hero} alt="Deuce's welder at work in the fabrication shop" className="w-full h-[120%] object-cover object-center opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/40 to-ink" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 42%, rgba(107,255,61,0.10), transparent 60%)" }} />
      </motion.div>

      {/* Atmosphere layers */}
      <div className="absolute inset-0 blueprint-bg blueprint-anim opacity-50" />
      <BlueprintLines />
      <Smoke count={5} />
      <Particles count={26} />
      <Sparks count={28} />

      <motion.div style={{ y: contentY, opacity: fade }} className="relative z-10 w-full">
        <Container className="py-28 flex flex-col items-center">
          {/* Logo plaque */}
          <motion.div
            className="relative mb-8 rounded-2xl border border-weld/25 bg-charcoal/70 backdrop-blur-sm p-3 sm:p-4 shadow-[0_0_50px_rgba(107,255,61,0.2)]"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE }}
          >
            <img src={logo} alt="Deuce's Welding & Fabrication logo" className="w-40 sm:w-48 h-auto rounded-lg" style={{ filter: "brightness(1.08) contrast(1.05)" }} />
          </motion.div>

          {/* Masked line-by-line reveal */}
          <h1 data-testid="hero-title" className="font-display text-7xl sm:text-8xl lg:text-9xl leading-[0.86]">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden">
                <motion.span
                  className={`block ${i === 1 ? "text-weld-glow text-glow" : "text-white"}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.35 + i * 0.14, duration: 0.9, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            className="mt-6 text-weld-glow font-semibold tracking-[0.22em] uppercase text-sm sm:text-base"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.75 }}
          >
            Mobile Welding • Custom Fabrication • Emergency Repairs
          </motion.p>

          <motion.div
            className="mt-10 flex flex-col sm:flex-row flex-wrap justify-center gap-4"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
          >
            <Button data-testid="hero-request-quote" size="lg" onClick={() => scrollTo("contact")}>
              Get a Free Quote <ArrowRight size={18} />
            </Button>
            <Button data-testid="hero-book" variant="outline" size="lg" href={BUSINESS.bookUrl} target="_blank" rel="noopener noreferrer">
              <Calendar size={18} /> Book Appointment
            </Button>
            <Button data-testid="hero-deposit" variant="outline" size="lg" href={BUSINESS.depositUrl} target="_blank" rel="noopener noreferrer">
              <CreditCard size={18} /> Pay Deposit
            </Button>
          </motion.div>
        </Container>
      </motion.div>

      <motion.button
        onClick={() => scrollTo("spotlight")}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/50 hover:text-weld-glow z-10"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        aria-label="Scroll down"
      >
        <ChevronDown size={28} />
      </motion.button>
    </section>
  );
};
